import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Skeleton,
  Alert,
  Button,
  Box,
  Chip
} from '@mui/material';
import {
  People,
  HourglassBottom,
  CancelScheduleSend,
  PersonAdd,
  Login,
  Groups,
  TrendingUp,
  AccountBalanceWallet,
  EventSeat,
  EventNote
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const MotionDiv = motion.div;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07 }
  }
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

// Gradient tokens — using GRADIENTS where defined, inline fallbacks for the rest
const GRADIENT_GREEN  = 'linear-gradient(135deg,#0E9F6E,#057A55)';
const GRADIENT_AMBER  = 'linear-gradient(135deg,#F59E0B,#D97706)';
const GRADIENT_ROSE   = 'linear-gradient(135deg,#E11D48,#9F1239)';
const GRADIENT_TEAL   = 'linear-gradient(135deg,#4A90A4,#2D6B7D)';
const GRADIENT_VIOLET = 'linear-gradient(135deg,#7C3AED,#5B21B6)';
const GRADIENT_BLUE = 'linear-gradient(135deg,#2563EB,#1E40AF)';

/**
 * Returns the ordered card definitions.
 * Values are derived from the API data object at render time.
 */
function buildCards(data, navigate, upcomingCount) {
  const { students, sessions, bookings, occupancy, revenue } = data;

  return [
    {
      key: 'total_students',
      title: 'Total Students',
      value: students.total,
      icon: <People />,
      gradient: GRADIENTS.navyLight,
      onClick: () => navigate('/librarian/students')
    },
    {
      key: 'upcoming_bookings',
      title: 'Upcoming Bookings',
      value: upcomingCount,
      icon: <EventNote />,
      gradient: GRADIENT_GREEN,
      onClick: () => navigate('/librarian/bookings')
    },
    {
      key: 'expiring_7d',
      title: 'Expiring ≤ 7d',
      value: students.expiring_within_7d,
      icon: <HourglassBottom />,
      gradient: GRADIENT_AMBER,
      onClick: () => navigate('/librarian/students?filter=expiring')
    },
    {
      key: 'no_plan',
      title: 'No / Expired Plan',
      value: students.expired_or_no_plan,
      icon: <CancelScheduleSend />,
      gradient: GRADIENT_ROSE,
      onClick: () => navigate('/librarian/students?filter=expired')
    },
    {
      key: 'new_month',
      title: 'New This Month',
      value: students.new_this_month,
      icon: <PersonAdd />,
      gradient: GRADIENTS.brand,
      onClick: null
    },
    {
      key: 'checked_in_today',
      title: 'Checked In Today',
      value: sessions.checked_in_today,
      icon: <Login />,
      gradient: GRADIENT_TEAL,
      onClick: null
    },
    {
      key: 'active_now',
      title: 'Active Now',
      value: sessions.active_now,
      icon: <Groups />,
      gradient: GRADIENTS.brand,
      onClick: () => navigate('/librarian/sessions')
    },
    {
      key: 'pending_bookings',
      title: 'Pending Bookings',
      value: bookings?.pending ?? 0,
      icon: <EventSeat />,
      gradient: GRADIENT_BLUE,
      onClick: () => navigate('/librarian/bookings')
    },
    {
      key: 'occupancy',
      title: 'Occupancy',
      value: `${occupancy.pct}%`,
      subtitle: `${occupancy.active_now}/${occupancy.capacity} seats`,
      icon: <TrendingUp />,
      gradient: GRADIENTS.coin,
      onClick: null
    },
    {
      key: 'revenue_month',
      title: 'Revenue (Month)',
      value: `₹${revenue.month_rupees.toLocaleString('en-IN')}`,
      subtitle: `Today ₹${revenue.today_rupees.toLocaleString('en-IN')}`,
      icon: <AccountBalanceWallet />,
      gradient: GRADIENT_VIOLET,
      onClick: () => navigate('/librarian/wallet')
    }
  ];
}

function AnalyticsCard({ card }) {
  const isClickable = Boolean(card.onClick);

  return (
    <MotionDiv variants={itemVariants} style={{ height: '100%' }}>
      <Card
        onClick={isClickable ? card.onClick : undefined}
        sx={{
          borderRadius: RADIUS.lg,
          background: card.gradient,
          color: 'white',
          overflow: 'hidden',
          cursor: isClickable ? 'pointer' : 'default',
          height: '100%',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease',
          ...(isClickable && {
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
            },
            '&:active': { transform: 'translateY(0)' }
          })
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Avatar
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                width: 36,
                height: 36
              }}
              aria-hidden="true"
            >
              {card.icon}
            </Avatar>
          </Box>

          <Typography
            variant="h4"
            sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.25 }}
            aria-label={`${card.title}: ${card.value}`}
          >
            {card.value}
          </Typography>

          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
            {card.title}
          </Typography>

          {card.subtitle && (
            <Typography
              variant="caption"
              sx={{ opacity: 0.75, display: 'block', mt: 0.25 }}
            >
              {card.subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </MotionDiv>
  );
}

AnalyticsCard.propTypes = {
  card: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    subtitle: PropTypes.string,
    icon: PropTypes.node.isRequired,
    gradient: PropTypes.string.isRequired,
    onClick: PropTypes.func
  }).isRequired
};

/**
 * LibrarianAnalyticsCards
 *
 * Presentational component — parent owns data fetching.
 * Renders 10 stat cards from the /api/librarian/analytics/overview response.
 */
function LibrarianAnalyticsCards({ data, loading, error, onRetry, upcomingCount }) {
  const navigate = useNavigate();

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <Box mb={2}>
        <Typography
          variant="overline"
          sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
        >
          Analytics
        </Typography>
        <Grid container spacing={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton
                variant="rounded"
                height={92}
                sx={{ borderRadius: RADIUS.lg }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error && !data) {
    return (
      <Box mb={2}>
        <Typography
          variant="overline"
          sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
        >
          Analytics
        </Typography>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={onRetry} color="inherit">
              Retry
            </Button>
          }
        >
          Couldn't load analytics
        </Alert>
      </Box>
    );
  }

  // ── Success state ──────────────────────────────────────────────────────────
  if (!data) return null;

  const cards = buildCards(data, navigate, upcomingCount);

  let updatedLabel = null;
  try {
    updatedLabel = formatDistanceToNow(new Date(data.as_of), { addSuffix: true });
  } catch {
    // date parse failed — omit the chip silently
  }

  return (
    <Box mb={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary' }}
        >
          Analytics
        </Typography>
        {updatedLabel && (
          <Chip
            label={`Updated ${updatedLabel}`}
            size="small"
            sx={{ fontSize: '0.65rem', height: 20, opacity: 0.7 }}
          />
        )}
      </Box>

      <MotionDiv
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Grid container spacing={2}>
          {cards.map((card) => (
            <Grid item xs={6} sm={4} md={3} key={card.key}>
              <AnalyticsCard card={card} />
            </Grid>
          ))}
        </Grid>
      </MotionDiv>
    </Box>
  );
}

LibrarianAnalyticsCards.propTypes = {
  /** Response data object from GET /api/librarian/analytics/overview */
  data: PropTypes.shape({
    library: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      capacity: PropTypes.number
    }),
    students: PropTypes.shape({
      total: PropTypes.number.isRequired,
      active_subscription: PropTypes.number.isRequired,
      expiring_within_7d: PropTypes.number.isRequired,
      expired_or_no_plan: PropTypes.number.isRequired,
      new_this_month: PropTypes.number.isRequired,
      blocked: PropTypes.number
    }).isRequired,
    bookings: PropTypes.shape({ pending: PropTypes.number }),
    sessions: PropTypes.shape({
      active_now: PropTypes.number.isRequired,
      checked_in_today: PropTypes.number.isRequired
    }).isRequired,
    occupancy: PropTypes.shape({
      active_now: PropTypes.number.isRequired,
      capacity: PropTypes.number.isRequired,
      pct: PropTypes.number.isRequired
    }).isRequired,
    revenue: PropTypes.shape({
      today_rupees: PropTypes.number.isRequired,
      today_paise: PropTypes.number,
      month_rupees: PropTypes.number.isRequired,
      month_paise: PropTypes.number
    }).isRequired,
    as_of: PropTypes.string.isRequired
  }),
  /** True while the analytics fetch is in-flight */
  loading: PropTypes.bool.isRequired,
  /** True when the last analytics fetch resulted in an error */
  error: PropTypes.bool.isRequired,
  /** Called when the user taps the Retry button in the error state */
  onRetry: PropTypes.func.isRequired,
  /** Count of upcoming bookings, shown on the Upcoming Bookings card */
  upcomingCount: PropTypes.number
};

LibrarianAnalyticsCards.defaultProps = {
  data: null,
  upcomingCount: 0
};

export default LibrarianAnalyticsCards;
